<template>
	<template v-if="isMobile">
		<DashFilter :mainfilters="mainfilters" @toggleMainFilter="toggleMainFilter" @toggleSubFilter="toggleSubFilter" />

		<section>
			<el-row class="flex flex--aligncenter">
				<el-col :span="22">
					<h2 class="no-margin">Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
				</el-col>
				<el-col :span="2" class="flex flex--justifyend">
					<SortIcon class="sorticon" @click="openTableSortDrawer = true" />
				</el-col>
			</el-row>

			<CasesTable
				:tableData="filteredCases"
				:loading="casesTableLoading"
				:totalCount="casesTotalCount"
				@loadNextPage="loadNextPage"
				:mobileBox="true"
				:sort="tableSort"
			/>
		</section>
		<TableSortDrawer v-model:open="openTableSortDrawer" :currentSort="tableSort" @setSort="(sort) => (tableSort = sort)" />
	</template>

	<template v-else>
		<section id="dashfilter">
			<DashFilter :mainfilters="mainfilters" @toggleMainFilter="toggleMainFilter" @toggleSubFilter="toggleSubFilter" />
		</section>

		<div class="bg--white">
			<section id="searchresults">
				<h2>Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
				<CasesTable :tableData="filteredCases" :loading="casesTableLoading" :totalCount="casesTotalCount" @loadNextPage="loadNextPage" />
			</section>
		</div>
	</template>
</template>

<script lang="ts">
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import CasesTable from '@/components/tables/CasesTable.vue';
import TheButton from '@/components/TheButton.vue';
import TheForm from '@/components/TheForm.vue';
import { searchCases } from '@/requests/searches.js';
import { CaseResponse, CaseState, SearchCaseRequest, TableSort } from '@/types/types';

import { Close, Plus } from '@element-plus/icons-vue';
import { defineComponent, computed } from 'vue';
import SortIcon from '../assets/icons/SortIcon.vue';
import TableSortDrawer from '../components/TableSortDrawer.vue';
import DashFilter, { FilterItem } from '@/components/DashFilter.vue';
import { useStore } from '../store';

export default defineComponent({
	name: 'FaelleWerberatView',
	data() {
		return {
			request: {
				searchTerm: '',
				category: null,
				medium: null,
				states: null,
				isPublished: null,
				fromDate: null,
				toDate: null,
				reach: null,
				sector: null,
			},
			mainfilters: [
				{
					key: 'MyCases',
					label: 'Meine Entscheidungen',
					active: true,
					subfilters: [
						{ key: CaseState.DecisionStop, label: 'STOPP', active: false, count: 0 },
						{ key: CaseState.DecisionSensitization, label: 'Sensibilisierung', active: false, count: 0 },
						{ key: CaseState.DecisionNoReason, label: 'Kein Grund', active: false, count: 0 },
						{ key: 'IWasBiased', label: 'Befangen', active: false, count: 0 },
					],
				},
				{
					key: 'AllCases',
					label: 'Alle Entscheidungen',
					active: false,
					subfilters: [
						{ key: CaseState.DecisionStop, label: 'STOPP', active: false, count: 0 },
						{ key: CaseState.DecisionSensitization, label: 'Sensibilisierung', active: false, count: 0 },
						{ key: CaseState.DecisionNoReason, label: 'Kein Grund', active: false, count: 0 },
					],
				},
			] as FilterItem[],
			id: 'searchForm',
			dateRange: null,
			selectedTimePeriod: [],
			tablePage: 0,
			itemsPerPage: 14,
			filteredCases: [] as CaseResponse[],
			casesTotalCount: 0,
			casesTableLoading: false,
			casesFilters: [] as any[],
			openTableSortDrawer: false,
			tableSort: { prop: 'voteEndDate', order: 'ascending' } as TableSort, // Current sort option
			firstLoad: false,
		};
	},
	components: {
		ButtonBack,
		TheButton,
		TheForm,
		Plus,
		Close,
		CasesTable,
		SortIcon,
		TableSortDrawer,
		DashFilter,
	},
	computed: {
		mainfilter_active(): FilterItem {
			return this.mainfilters.find((m) => m.active) || this.mainfilters[0];
		},
	},
	setup() {
		const store = useStore();
		const isMobile = computed(() => store.state.isMobile);

		return {
			isMobile,
		};
	},
	methods: {
		async loadCases(filters) {
			this.casesTableLoading = true;
			const werberatSearchRequest: SearchCaseRequest = {
				searchTerm: null,
				fromDate: null,
				toDate: null,
				states: null,
				isPublished: null,
				category: null,
				medium: null,
				reach: null,
				sector: null,
			};
			if (this.mainfilter_active.key === 'AllCases') {
				werberatSearchRequest.isPublished = true;
			}
			const stateFilters = filters.filter((f) => f !== 'IWasBiased').map((filter) => filter as unknown as CaseState);
			if (stateFilters.length > 0) {
				werberatSearchRequest.states = stateFilters;
			} else {
				werberatSearchRequest.states = null;
			}
			if (filters.includes('IWasBiased')) {
				werberatSearchRequest.iWasBiased = true;
			}

			const caseResult = await searchCases(werberatSearchRequest, 0, 20, true);

			if (caseResult.success) {
				this.casesTotalCount = caseResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = caseResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...caseResult.data.items];
				}

				// only update counts of active filters
				this.mainfilter_active.subfilters = this.mainfilter_active.subfilters.map((f) => {
					if (f.key === CaseState.DecisionStop) {
						f.count = caseResult.data.counts.find((c) => c.item1 === CaseState.DecisionStop.toString())?.item2 || 0;
					} else if (f.key === CaseState.DecisionSensitization) {
						f.count = caseResult.data.counts.find((c) => c.item1 === CaseState.DecisionSensitization.toString())?.item2 || 0;
					} else if (f.key === CaseState.DecisionNoReason) {
						f.count = caseResult.data.counts.find((c) => c.item1 === CaseState.DecisionNoReason.toString())?.item2 || 0;
					} else if (f.key === 'IWasBiased') {
						f.count = caseResult.data.counts.find((c) => c.item1 === 'IWasBiased')?.item2 || 0;
					}
					return f;
				});
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}

			// on mobile: make call for other mainfilter to update the counts! (cause mobile all filters are visible and should not be 0)
			if (this.isMobile && this.firstLoad === false) {
				const werberatSearchRequestOther = {
					...werberatSearchRequest,
					isPublished: werberatSearchRequest.isPublished === true ? false : true, // opposite of mainfilter
				};

				const caseResultOther = await searchCases(werberatSearchRequestOther, 0, 20, true);
				if (caseResultOther.success) {
					const mainfilter_not_active = this.mainfilters.find((f) => f.active === false);

					// only update counts of active filters
					mainfilter_not_active.subfilters = mainfilter_not_active.subfilters.map((f) => {
						if (f.key === CaseState.DecisionStop) {
							f.count = caseResultOther.data.counts.find((c) => c.item1 === CaseState.DecisionStop.toString())?.item2 || 0;
						} else if (f.key === CaseState.DecisionSensitization) {
							f.count = caseResultOther.data.counts.find((c) => c.item1 === CaseState.DecisionSensitization.toString())?.item2 || 0;
						} else if (f.key === CaseState.DecisionNoReason) {
							f.count = caseResultOther.data.counts.find((c) => c.item1 === CaseState.DecisionNoReason.toString())?.item2 || 0;
						} else if (f.key === 'IWasBiased') {
							f.count = caseResultOther.data.counts.find((c) => c.item1 === 'IWasBiased')?.item2 || 0;
						}
						return f;
					});
				}
				this.firstLoad = true;
			}

			this.casesTableLoading = false;
		},
		loadNextPage() {
			this.tablePage++;

			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		toggleMainFilter(filter) {
			this.tablePage = 0;
			this.mainfilters = this.mainfilters.map((m) => {
				// set all mainfilters to false except current one
				return {
					...m,
					active: m.key === filter ? true : false,
				};
			});

			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		toggleSubFilter(filter) {
			this.tablePage = 0;
			const findSub = this.mainfilter_active.subfilters.find((f) => f.key === filter);
			if (findSub) {
				findSub.active = !findSub.active;
			}

			this.loadCases(this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key));
		},
	},
	mounted() {
		this.loadCases([]);
	},
});
</script>
